import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider, Helmet } from '@dr.pogodin/react-helmet';
import { fetchAndUpdateFavicon } from "./utils/faviconUtils";
import { initializeFontSystem } from "./utils/fontInitializer";

import NavBar from './components/NavBar';
import Footer from './components/Footer';
import FooterPage from './pages/FooterPage';

import Shop from './pages/Shop';
import Home from './pages/Home';
import ProductDetails from './pages/ProductDetails';
import ProductCategory from './pages/ProductCategory';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import VerifyCustomerPage from './pages/VerifyCustomerPage';
import Cart from './components/Cart';
import UserMenu from './components/UserMenu';
import ProtectedRoute from './components/ProtectedRoute';
import AccountSettings from './pages/AccountSettings';
import Wishlist from './pages/Wishlist';
import OrdersHistory from './pages/OrdersHistory';
import OrderDetails from './pages/OrderDetails';
import OfferPage from './pages/OfferPage';
import FeedbackForm from './components/FeedbackForm';
import FeedbackHistory from './pages/FeedbackHistory';
import PasswordResetPage from './pages/PasswordResetPage';

import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';

import './styles/fonts.css';
import './App.css';

function App() {
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [browserTitle, setBrowserTitle] = useState('Clothing Store');

    // Fetch and update favicon, font system, and browser title on mount
    useEffect(() => {
        fetchAndUpdateFavicon();
        initializeFontSystem();
        
        // Fetch browser title from company name
        const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const companyCode = process.env.REACT_APP_COMPANY_CODE || 'C001';
        
        fetch(`${BASE_URL}/api/admin/website/browser-title?company_code=${companyCode}`)
          .then(res => res.json())
          .then(data => {
            if (data.success && data.browser_title) {
              setBrowserTitle(data.browser_title);
            }
          })
          .catch(err => {
            console.error('Error fetching browser title:', err);
          });
    }, []);

    // Hide navbar and footer on specific routes (exact pathname matches)
    const hideNavbarRoutes = ['/verify-customer', '/login', '/reset-password', '/register'];
    // Use startsWith to ignore query strings/search params
    const hideNavbar = hideNavbarRoutes.some((p) => location.pathname === p || location.pathname.startsWith(p + '?'));

    // Listen for sidebar state changes from NavBar
    useEffect(() => {
        const handleSidebarStateChange = (event) => {
            setSidebarOpen(event.detail.isOpen);
        };

        window.addEventListener('sidebarStateChange', handleSidebarStateChange);

        return () => {
            window.removeEventListener('sidebarStateChange', handleSidebarStateChange);
        };
    }, []);

    return (
        <HelmetProvider>
            <Helmet>
                <title>{browserTitle}</title>
            </Helmet>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                <AuthProvider>
                    <CartProvider>
                        <div className="App">
                            {!hideNavbar && <NavBar onSidebarStateChange={setSidebarOpen} />}
                    {/* Main Content Wrapper */}
                    <div className={`main-content-wrapper ${sidebarOpen ? 'sidebar-open' : ''}`}>
                        <main>
                            <Routes>
                                {/* Public Routes */}
                                <Route path="/" element={<Home />} />
                                <Route path="/shop/:category" element={<Shop />} />
                                <Route path="/product/:style_number" element={<ProductDetails />} />
                                <Route path="/shop/:category/:productType" element={<ProductCategory />} />
                                <Route path="/register" element={<RegisterPage />} />
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/verify-customer" element={<VerifyCustomerPage />} />
                                <Route path="/reset-password" element={<PasswordResetPage />} />
                                <Route path="/cart" element={<Cart />} />
                                <Route path="/account-settings" element={<AccountSettings />} />
                                <Route path="/wishlist" element={<Wishlist />} />
                                <Route path="/orders-history" element={<OrdersHistory />} />
                                <Route path="/orders/:orderId" element={<OrderDetails />} />
                                <Route path="/offers" element={<OfferPage />} />
                                <Route path="/feedback" element={<FeedbackForm />} />
                                <Route path="/order-review/:orderId" element={<FeedbackForm />} />
                                <Route path="/feedback-history" element={<FeedbackHistory />} />
                                <Route path="/:slug" element={<FooterPage />} />


                                {/* Protected Routes */}
                                <Route path="/usermenu" element={
                                    <ProtectedRoute>
                                        <UserMenu />
                                    </ProtectedRoute>
                                } />
                            </Routes>
                        </main>
                        {!hideNavbar && <Footer />}
                    </div>
                </div>
            </CartProvider>
        </AuthProvider>
    </GoogleOAuthProvider>
    </HelmetProvider>
    );
}

export default App;